import * as React from "react";
import * as _ from "lodash";
import { IPatientConcern } from "@curefit/ehr-common";
import { PatientPersonalProfile, DailyRoutine } from "@curefit/ups-common";
import ErrorContainer from "./../../containers/ErrorContainer"
import {EditViewDisplayMode} from "../common/EditViewCard";
import IEditedBy from "../../models/IEditedBy";
import IinfoQues from "../../models/staticData/IinfoQues";
import HabitEditViewContainer from "../../containers/EditView/HabitEditViewContainer";
import IHabitStaticData from "../../models/staticData/IHabitStaticData";
import MindfulnessEditViewCont from "../../containers/EditView/MindfulnessEditViewCont";
import IRoutineActivity from "../../models/staticData/IRoutineActivities";
import RoutineAttribute from "../../models/staticData/RoutineAttribute";
import {ITypeFormResponse} from "../../models/ITypeFormResponse";
import DietRecallEditViewCont from "../../containers/EditView/DietRecallEditViewCont";
import IStaticDataValue from "../../../../common/models/IStaticDataValue";
import ModelFactory from "../../models/ModelFactory";
import IDishCatalogueResponse from "../../models/maverick/IDishCatalogueResponse";
import {AppMessages} from "../../utils/AppConstants";
import DetailsEditViewCont from "../../containers/EditView/DetailsEditViewCont";
import AppUtils from "../../utils/AppUtils";
import IDietRecall from "../../models/IDietRecall";
import CTA, {CTATheme} from "../common/CallToAction";

export interface PatientLifestyleTabProps {
    patientId: number;
    curefitUserId: number;
    patientConcerns: IPatientConcern[];
    editedBy: IEditedBy,
    staticPersonalInfo: IinfoQues,
    staticPatientHabits: IHabitStaticData,
    patientPersonalProfile: PatientPersonalProfile,
    saveEditedPatientProfile: (patientPersonalProfile: PatientPersonalProfile) => void,
    staticMindfulnessInfo: IinfoQues,
    staticFitnessRecallInfo: IinfoQues,
    fetchStaticSymptomsData: (input: string) => Promise<any>,
    routineActivities: IRoutineActivity[]
    fetchStaticRoutineAttributes: () => Promise<RoutineAttribute>,
    fetchFitnessActivities: (input: string) => Promise<any[]>,
    fetchMindfulnessActivities: (input: string) => Promise<any[]>,
    staticFoodTypes: IStaticDataValue[],
    staticCuisines: IStaticDataValue[],
    patientTypeFormData: ITypeFormResponse;
    typeFormMapping: any;
    savePatientProfile: (curefitUserId: number, patientId: number, isSelf: boolean, patientPersonalProfile: PatientPersonalProfile) => Promise<any>;
    fetchDishByDishName: (input: string) => Promise<IDishCatalogueResponse[]>;
    nextTab: () => void;
    isSelf: boolean;
    appointmentSchedule: any
}


export interface PatientLifestyleTabState {
    error: string,
    errorDetails?: string,
    isSaving?: boolean,
    modalScreen: number
}

export default class PatientLifestyleTab extends React.Component<PatientLifestyleTabProps, PatientLifestyleTabState> {
    private mindfulnessInfoRef: MindfulnessEditViewCont;
    private detailsInfoRef: DetailsEditViewCont;
    private habitsRef: HabitEditViewContainer;
    private dietRecallRef: DietRecallEditViewCont;

    constructor(props: PatientLifestyleTabProps) {
        super(props);
        this.state = {
            error: null,
            isSaving: false,
            modalScreen: null
        }
    }
    componentWillUnmount() {
        if (AppUtils.isAILCConsultation(this.props.appointmentSchedule)) {
            this.saveAILCEditedLifestyle();
        } else {
            this.saveEditedLifestyle();
        }
    }

    saveAILCEditedLifestyle = () => {
        let dietRecall = this.dietRecallRef.getSaveObject();
        let personalInformation = this.detailsInfoRef.getSaveObject();
        let updatedPersonalInfo = { ...this.props.patientPersonalProfile, dietRecall, personalInformation };
        this.props.saveEditedPatientProfile(updatedPersonalInfo as any);
    }
    saveEditedLifestyle = () => {
        let dailyRoutine = this.mindfulnessInfoRef.getSaveObject();
        let habits = this.habitsRef.getSaveObj();
        let dietRecall = this.dietRecallRef.getSaveObject();
        let updatedPersonalInfo = { ...this.props.patientPersonalProfile, dailyRoutine, habits, dietRecall };
        this.props.saveEditedPatientProfile(updatedPersonalInfo as any);
    }

    saveAILCPatientLifestyle = () => {
        let dietRecall = AppUtils.sanitizeDietRecallRequest(this.dietRecallRef.getSaveObject());
        let personalInformation = this.detailsInfoRef.getSaveObject();
        let updatedPersonalInfo = { ...this.props.patientPersonalProfile, dietRecall, personalInformation };
        this.props.savePatientProfile(this.props.curefitUserId, this.props.patientId, this.props.isSelf, updatedPersonalInfo as any).then(() => {
            console.log("Patient lifestyle information saved successfully");
            this.props.nextTab();
        }).catch((err: any) => {
            this.setState({
                error: AppMessages.GOALS_SAVE_ERROR,
                errorDetails: err.message,
                isSaving: false
            })
        });
    }
    savePatientLifestyle = () => {
        let dailyRoutine = this.mindfulnessInfoRef.getSaveObject();
        let habits = this.habitsRef.getSaveObj();
        let dietRecall = AppUtils.sanitizeDietRecallRequest(this.dietRecallRef.getSaveObject());
        let updatedPersonalInfo = { ...this.props.patientPersonalProfile, dailyRoutine, habits, dietRecall };
        this.props.savePatientProfile(this.props.curefitUserId, this.props.patientId, this.props.isSelf, updatedPersonalInfo as any).then(() => {
            console.log("Patient lifestyle information saved successfully");
            this.props.nextTab();
        }).catch((err: any) => {
            this.setState({
                error: AppMessages.GOALS_SAVE_ERROR,
                errorDetails: err.message,
                isSaving: false
            })
        });
    }
    clearErrorMessage = () => {
        this.setState({
            error: null,
            errorDetails: null
        })
    }

    render() {
        let {patientPersonalProfile} = this.props;
        let personalInformation = patientPersonalProfile && patientPersonalProfile.personalInformation ? patientPersonalProfile.personalInformation : {};
        let consumptionFrequency = patientPersonalProfile && patientPersonalProfile.habits && patientPersonalProfile.habits.genericHabits ? patientPersonalProfile.habits.genericHabits : null;
        let foodDislikes = patientPersonalProfile && patientPersonalProfile.foodDislikes ? patientPersonalProfile.foodDislikes : null;
        let workoutPreference = patientPersonalProfile && patientPersonalProfile.habits && patientPersonalProfile.habits.fitnessHabit && patientPersonalProfile.habits.fitnessHabit.workoutPreference ?
                                patientPersonalProfile.habits.fitnessHabit.workoutPreference : null;
        let meditationPreference = patientPersonalProfile && patientPersonalProfile.habits && patientPersonalProfile.habits.fitnessHabit && patientPersonalProfile.habits.fitnessHabit.meditationPreference ?
                                   patientPersonalProfile.habits.fitnessHabit.meditationPreference : null;
        let dailyRoutine: DailyRoutine = patientPersonalProfile && patientPersonalProfile.dailyRoutine ? patientPersonalProfile.dailyRoutine : null;
        let details = patientPersonalProfile && patientPersonalProfile.personalInformation ? patientPersonalProfile.personalInformation : null;
        let dietInfo = patientPersonalProfile && patientPersonalProfile.dietRecall ? patientPersonalProfile.dietRecall : ModelFactory.createNewDietRecall();
        let questionnaire = !_.isEmpty(this.props.patientTypeFormData) && !_.isEmpty(this.props.patientTypeFormData.userForm) &&
        !_.isEmpty(this.props.patientTypeFormData.userForm.questionResponseDetails) ? this.props.patientTypeFormData.userForm.questionResponseDetails : [];
        let errorMessage = !_.isNull(this.state.error) ? this.state.error : AppMessages.VITALS_AND_ALLERGIES_SAVE_ERROR;
        let errorDetails = !_.isNull(this.state.errorDetails) ? this.state.errorDetails : "";
        return <div className="patient-lifestyle-container">
            {/*<ScrollToContainer componentToScroll={document.getElementsByClassName("ongoing-consultation-main-cont")[0]} scrollItems={this.generateScrollItems()}/>*/}
            {
                !_.isNull(this.state.error) &&
                <ErrorContainer errorText={errorMessage} errorDetails={errorDetails} errorCode={0} closeError={this.clearErrorMessage} />
            }
            {
                !AppUtils.isAILCConsultation(this.props.appointmentSchedule) &&
                <div className="patient-info-subsection mindfulness-section">
                    <MindfulnessEditViewCont mindfulness={dailyRoutine} staticMindfulnessInfo={this.props.staticMindfulnessInfo}
                        displayMode={EditViewDisplayMode.EDIT} ref={(mindfulnessInfoRef) => this.mindfulnessInfoRef = mindfulnessInfoRef}/>
                </div>
            }
            {
                AppUtils.isAILCConsultation(this.props.appointmentSchedule) &&
                    <div className="patient-info-subsection mindfulness-section">
                        <DetailsEditViewCont details={details} staticPersonalInfo={this.props.staticPersonalInfo}
                            displayMode={EditViewDisplayMode.EDIT} ref={(detailsInfoRef) => this.detailsInfoRef = detailsInfoRef}
                            fetchDishByDishName={this.props.fetchDishByDishName}/>
                    </div>
            }
            <div className="patient-info-subsection dietInfo-section">
                <DietRecallEditViewCont ref={(dietRecallDOM) => this.dietRecallRef = dietRecallDOM}
                                        patientId={this.props.patientId} dietInfo={dietInfo as IDietRecall}
                                        displayMode={EditViewDisplayMode.EDIT} fetchDishByDishName={this.props.fetchDishByDishName}
                                        staticFoodTypes={this.props.staticFoodTypes} staticCuisines={this.props.staticCuisines}
                                        questionnaire={questionnaire} typeFormMapping={this.props.typeFormMapping}/>
            </div>
             {
                !AppUtils.isAILCConsultation(this.props.appointmentSchedule) &&
                <div className="patient-info-subsection habit-section">
                    <HabitEditViewContainer meditationPreference={meditationPreference} workoutPreference={workoutPreference}
                                            displayMode={EditViewDisplayMode.EDIT} staticPatientHabits={this.props.staticPatientHabits}
                                            fetchFitnessActivities={this.props.fetchFitnessActivities} fetchMindfulnessActivities={this.props.fetchMindfulnessActivities}
                                            fetchDishByDishName={this.props.fetchDishByDishName} consumptionFrequency={consumptionFrequency}
                                            foodDislikes={foodDislikes} ref={(habitsRef) => this.habitsRef = habitsRef}/>
                </div>
             }
            <div className={"save-button collapsible-card"}>
                <CTA innerText={"Save"} theme={CTATheme.PRIMARY} onClick={AppUtils.isAILCConsultation(this.props.appointmentSchedule) ? this.saveAILCPatientLifestyle : this.savePatientLifestyle} customStyle={{display: "inline"}}/>
            </div>
        </div>
    }
}